import { pokerGraphqlRequest } from '../Utils/PokerGraphqlClient';
import type {
  CashHandHistoryPage,
  CashHistoryFilters,
  CashHandDetails,
  CurrencyOption,
  HistoryGameType,
  HistorySortDirection,
  TournamentHistoryPage,
  TournamentHandHistoryPage,
  TournamentHistoryFilters,
  TournamentLeaderboardEntry,
} from './Type/HistoryTypes';
import {
  CASH_HISTORY_QUERY,
  CASH_CURRENCY_OPTIONS_QUERY,
  CASH_HAND_DETAILS_QUERY,
  MTT_HISTORY_QUERY,
  MTT_HANDS_QUERY,
  TOURNAMENT_LEADERBOARD_QUERY,
} from './GraphQlQuery/HistoryGraphqlQueries';

export const HistoryHelper = {
  getCashHandHistory: async (args: {
    userId: string;
    filters?: CashHistoryFilters;
    limit?: number;
    cursor?: string | number | null;
    sortBy?: string;
    sortDirection?: HistorySortDirection;
    signal?: AbortSignal;
  }): Promise<CashHandHistoryPage> => {
    const variables: Record<string, unknown> = {
      userId: args.userId,
      limit: args.limit,
      cursor: args.cursor ?? undefined,
      ...(args.filters ?? {}),
      sortBy: args.sortBy,
      sortDirection: args.sortDirection,
    };

    const res = await pokerGraphqlRequest<
      { userCashHandHistory: CashHandHistoryPage },
      Record<string, unknown>
    >({
      query: CASH_HISTORY_QUERY,
      variables,
      signal: args.signal,
    });

    return res.userCashHandHistory;
  },

  getTournamentHistory: async (args: {
    userId: string;
    filters?: TournamentHistoryFilters;
    limit?: number;
    cursor?: string | number | null;
    sortBy?: string;
    sortDirection?: HistorySortDirection;
    signal?: AbortSignal;
  }): Promise<TournamentHistoryPage> => {
    const variables: Record<string, unknown> = {
      userId: args.userId,
      limit: args.limit,
      cursor: args.cursor ?? undefined,
      ...(args.filters ?? {}),
      sortBy: args.sortBy,
      sortDirection: args.sortDirection,
    };

    const res = await pokerGraphqlRequest<
      { userTournamentHistory: TournamentHistoryPage },
      Record<string, unknown>
    >({
      query: MTT_HISTORY_QUERY,
      variables,
      signal: args.signal,
    });

    return res.userTournamentHistory;
  },

  getTournamentHandHistory: async (args: {
    userId: string;
    tournamentGameId: string;
    limit?: number;
    cursor?: string | number | null;
    signal?: AbortSignal;
  }): Promise<TournamentHandHistoryPage> => {
    const res = await pokerGraphqlRequest<
      { userTournamentHandHistory: TournamentHandHistoryPage },
      { userId: string; tournamentGameId: string; limit?: number; cursor?: string | number | null }
    >({
      query: MTT_HANDS_QUERY,
      variables: {
        userId: args.userId,
        tournamentGameId: args.tournamentGameId,
        limit: args.limit,
        cursor: args.cursor ?? undefined,
      },
      signal: args.signal,
    });

    return res.userTournamentHandHistory;
  },

  getCashHandDetails: async (args: {
    userId: string;
    gameId: string;
    signal?: AbortSignal;
  }): Promise<CashHandDetails> => {
    const res = await pokerGraphqlRequest<
      { userCashHandDetails: CashHandDetails },
      { userId: string; gameId: string }
    >({
      query: CASH_HAND_DETAILS_QUERY,
      variables: {
        userId: args.userId,
        gameId: args.gameId,
      },
      signal: args.signal,
    });

    return res.userCashHandDetails;
  },

  getCashCurrencyOptions: async (args: {
    gameType?: HistoryGameType;
    signal?: AbortSignal;
  }): Promise<CurrencyOption[]> => {
    const res = await pokerGraphqlRequest<
      { cashCurrencyOptions: CurrencyOption[] },
      { gameType?: HistoryGameType }
    >({
      query: CASH_CURRENCY_OPTIONS_QUERY,
      variables: {
        gameType: args.gameType,
      },
      signal: args.signal,
    });

    return res.cashCurrencyOptions;
  },

  getTournamentLeaderboard: async (args: {
    tournamentGameId: string;
    signal?: AbortSignal;
  }): Promise<TournamentLeaderboardEntry[]> => {
    const res = await pokerGraphqlRequest<
      { tournamentLeaderboard: TournamentLeaderboardEntry[] },
      { tournamentGameId: string }
    >({
      query: TOURNAMENT_LEADERBOARD_QUERY,
      variables: {
        tournamentGameId: args.tournamentGameId,
      },
      signal: args.signal,
    });

    return res.tournamentLeaderboard;
  },
};
